'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Search, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { useCartStore } from '@/stores/cart';
import { useVehicleHistoryStore } from '@/stores/vehicle-history';
import { useVehicleStore } from '@/stores/vehicle';
import { toast } from 'sonner';
import { useLocale } from '@/hooks/useLocale';
import { t } from '@/i18n/t';

interface VinLookupProps {
  onComplete?: () => void;
  stayOnPage?: boolean;
}

export function VinLookup({ onComplete, stayOnPage }: VinLookupProps = {}) {
  const [vin, setVin] = useState('');
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const setVehicle = useCartStore((state) => state.setVehicle);
  const addEntry = useVehicleHistoryStore((state) => state.addEntry);
  const setCurrentVehicle = useVehicleStore((state) => state.setVehicle);
  const { locale } = useLocale();

  const handleLookup = async () => {
    const cleanVin = vin.replace(/\s+/g, '').toUpperCase();
    if (!cleanVin || !/^[A-HJ-NPR-Z0-9]{17}$/.test(cleanVin)) {
      toast.error(t(locale, 'vin.invalidVin'));
      return;
    }

    setLoading(true);
    try {
      const result = await api.lookupVin(cleanVin);

      if (result.ktype && result.vehicle) {
        setVehicle(result.vehicle, null, 'vin-sokning');
        addEntry(cleanVin, result.ktype, result.vehicle);
        setCurrentVehicle(result.vehicle, cleanVin);
        toast.success(t(locale, 'vin.found', { make: result.vehicle.manufacturer, model: result.vehicle.model }));
        onComplete?.();
        if (!stayOnPage) {
          router.push(`/fordon/${result.ktype}?vin=${cleanVin}`);
        }
      } else {
        toast.error(t(locale, 'vin.notFound'));
      }
    } catch (error) {
      toast.error(t(locale, 'vin.error', { message: error instanceof Error ? error.message : t(locale, 'registration.unknownError') }));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleLookup();
  };

  return (
    <form onSubmit={handleSubmit} className="flex gap-2">
      <div className="flex-1">
        <Input
          value={vin}
          onChange={(e) => setVin(e.target.value.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/gi, ''))}
          placeholder={t(locale, 'vin.placeholder')}
          maxLength={17}
          className="h-12 text-center font-mono tracking-wider text-base uppercase bg-white text-gray-900 placeholder:text-gray-400"
          disabled={loading}
        />
      </div>
      <Button type="submit" disabled={loading || vin.length !== 17} size="lg" className="h-12">
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Search className="h-4 w-4 mr-2" />
        )}
        {loading ? '' : t(locale, 'vin.searchVehicle')}
      </Button>
    </form>
  );
}
